import React, { useEffect, useState } from "react";
import { Plus, X, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const EMPTY = {
  full_name: "",
  company: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
  phone: "",
  is_default: false,
};

export default function MailingAddresses() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | {address}
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/addresses").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/auth/addresses", form);
        toast.success("ADDRESS ADDED");
      } else {
        await api.put(`/auth/addresses/${editing.id}`, form);
        toast.success("ADDRESS UPDATED");
      }
      close(); load();
    } catch (err) {
      toast.error("FAILED", { description: err?.response?.data?.detail || "" });
    } finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove address?`)) return;
    await api.delete(`/auth/addresses/${a.id}`);
    toast.success("REMOVED"); load();
  };

  const setDefault = async (a) => {
    await api.put(`/auth/addresses/${a.id}/default`);
    toast.success("SET AS DEFAULT"); load();
  };

  return (
    <div className="mt-8" data-testid="mailing-addresses">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="label-mono text-[#CFF048]">// MAILING ADDRESSES</p>
          <h2 className="font-display font-black text-2xl uppercase mt-1">Saved Addresses</h2>
        </div>
        <p className="label-mono text-[#71717A]">{list.length} SAVED</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((a) => (
          <div key={a.id} className="bg-[#0A0A0A] border border-[#27272A] p-5 relative" data-testid={`address-card-${a.id}`}>
            {a.is_default && (
              <span className="absolute top-3 right-3 sticker text-[#CFF048] border-[#CFF048]">
                <Star size={10} className="inline mr-1 fill-[#CFF048]" /> DEFAULT
              </span>
            )}
            <p className="font-display font-bold uppercase text-base">{a.full_name}</p>
            {a.company && <p className="text-sm text-[#A1A1AA] mt-1">{a.company}</p>}
            <p className="text-sm text-[#A1A1AA] mt-2">{a.line1}</p>
            {a.line2 && <p className="text-sm text-[#A1A1AA]">{a.line2}</p>}
            <p className="text-sm text-[#A1A1AA] mt-1">{a.city}, {a.state} {a.pincode}</p>
            <p className="text-sm text-[#A1A1AA]">{a.country}</p>
            <p className="font-mono-cult text-sm text-[#71717A] mt-2">{a.phone}</p>
            <div className="border-t border-[#27272A] mt-4 pt-3 flex items-center gap-4 label-mono">
              <button onClick={() => openEdit(a)} className="text-white hover:text-[#CFF048] underline" data-testid={`edit-address-${a.id}`}>Edit</button>
              <button onClick={() => remove(a)} className="text-white hover:text-red-500 underline" data-testid={`remove-address-${a.id}`}>Remove</button>
              {!a.is_default && (
                <button onClick={() => setDefault(a)} className="text-[#CFF048] hover:underline ml-auto" data-testid={`default-address-${a.id}`}>Set Default</button>
              )}
            </div>
          </div>
        ))}

        {/* Add tile */}
        <button
          onClick={openNew}
          className="bg-[#0A0A0A] border-2 border-dashed border-[#27272A] hover:border-[#CFF048] hover:text-[#CFF048] text-[#71717A] p-5 min-h-[200px] flex flex-col items-center justify-center gap-3 transition-colors"
          data-testid="add-address-btn"
        >
          <Plus size={28} />
          <span className="label-mono">ADD A NEW ADDRESS</span>
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={close}>
          <form onSubmit={save} className="bg-[#0A0A0A] border border-[#27272A] max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="address-modal">
            <div className="flex justify-between items-center p-5 border-b border-[#27272A]">
              <p className="label-mono text-[#CFF048]">// {editing === "new" ? "ADD ADDRESS" : "EDIT ADDRESS"}</p>
              <button type="button" onClick={close}><X size={18} /></button>
            </div>
            <div className="p-5 grid sm:grid-cols-2 gap-3">
              {[
                ["full_name", "FULL NAME", "sm:col-span-2", true],
                ["company", "COMPANY (OPTIONAL)", "sm:col-span-2", false],
                ["line1", "ADDRESS LINE 1", "sm:col-span-2", true],
                ["line2", "ADDRESS LINE 2", "sm:col-span-2", false],
                ["city", "CITY", "", true],
                ["state", "STATE", "", true],
                ["pincode", "PINCODE / ZIP", "", true],
                ["country", "COUNTRY", "", true],
                ["phone", "PHONE", "sm:col-span-2", true],
              ].map(([k, label, cls, required]) => (
                <input
                  key={k}
                  required={required}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  placeholder={label}
                  className={`cult-input ${cls || ""}`}
                  data-testid={`address-${k}`}
                />
              ))}
              <label className="flex items-center gap-2 label-mono sm:col-span-2 mt-1">
                <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="address-default" />
                SET AS DEFAULT
              </label>
            </div>
            <div className="p-5 border-t border-[#27272A] flex justify-end gap-3">
              <button type="button" onClick={close} className="btn-outline">CANCEL</button>
              <button type="submit" disabled={busy} className="btn-neon" data-testid="save-address-btn">
                {busy ? "SAVING..." : "SAVE ADDRESS"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
